import { FileButton, Loader, Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { Box, Image as ImageIcon, LibraryBig, Sparkles, Upload, X } from "lucide-react";
import { useRef, type ReactElement } from "react";

import { IMAGE_TYPES } from "../../../platform/media/ImagePicker";
import { UNSUPPORTED } from "../../../platform/media/ImageRefsSlot";
import classes from "../../../platform/media/slots.module.css";
import type { SourceView } from "../composer/source";

export interface Frame {
  /** The chosen frame, or null for the empty slot. */
  view: SourceView | null;
  onFromLibrary: () => void;
  /** Live's "Generate": its "Generate frame" dialog, the image saved into the frame (#985). */
  onGenerate: () => void;
  onFromProduct: () => void;
  /** Live's "Upload": the file the chooser returned (null when it was dismissed). */
  onUpload: (file: File | null) => void;
  /** An upload for the frame is running. */
  uploading: boolean;
  onRemove: () => void;
  /** Makes the slot the product popover's anchor. */
  wrap: (element: ReactElement) => ReactElement;
  /** The current setup takes no such frame (the end frame under Multi-Shot): greyed, closed. */
  disabled?: boolean;
  /** Live's slot tooltip: the mode's image limits (none where it has none, #260). */
  limits?: string[];
}

/**
 * A frame slot, the start frame's or the end frame's. It opens live's menu (capture 22): "From
 * library" and "From product" choose the image (G1 decisions 21 and 22), "Upload" uploads one,
 * and "Generate" makes one (#985). A chosen frame fills the slot, with a round remove control on
 * its corner.
 */
export function FrameSlot({ label, frame }: { label: string; frame: Frame }) {
  // Clears the chooser after each file, so the same file can be chosen again.
  const resetChooser = useRef<() => void>(null);
  const lower = label.toLowerCase();
  const { view } = frame;
  if (frame.disabled === true) {
    return (
      <Tooltip label={UNSUPPORTED} withArrow>
        {/* A disabled button takes no hover, so the tooltip hangs from a wrapper, as live's. */}
        <span className={classes.slotFilled}>
          <UnstyledButton
            className={view === null ? classes.slot : classes.slotChosen}
            aria-label={view === null ? label : `${label}: ${view.name}`}
            disabled
          >
            {view === null || view.url === null ? (
              <ImageIcon size={20} strokeWidth={1.5} />
            ) : (
              <img src={view.url} alt="" className={classes.slotPicture} />
            )}
            {view === null ? <span className={classes.slotLabel}>{label}</span> : null}
          </UnstyledButton>
        </span>
      </Tooltip>
    );
  }
  const slot =
    view === null ? (
      <UnstyledButton
        className={classes.slot}
        aria-label={label}
        aria-busy={frame.uploading}
      >
        {frame.uploading ? (
          <Loader size={20} aria-hidden />
        ) : (
          <ImageIcon size={20} strokeWidth={1.5} />
        )}
        <span className={classes.slotLabel}>{label}</span>
      </UnstyledButton>
    ) : (
      <UnstyledButton
        className={classes.slotChosen}
        aria-label={`${label}: ${view.name}`}
        aria-busy={frame.uploading}
      >
        {view.url === null ? (
          <ImageIcon size={20} strokeWidth={1.5} />
        ) : (
          <img src={view.url} alt="" className={classes.slotPicture} />
        )}
        {/* A replacement on its way spins over the frame it replaces. */}
        {frame.uploading ? <Loader size={20} className={classes.slotBusy} aria-hidden /> : null}
      </UnstyledButton>
    );
  return frame.wrap(
    <span className={classes.slotFilled}>
      {/* The chooser's input sits outside the menu, which unmounts as the chooser opens. */}
      <FileButton
        accept={IMAGE_TYPES.join(",")}
        resetRef={resetChooser}
        onChange={(file) => {
          frame.onUpload(file);
          resetChooser.current?.();
        }}
        inputProps={{ "aria-label": `Choose a ${lower} to upload` }}
      >
        {(chooser) => (
          // In a portal: the composer's header clips what overflows it.
          <Menu position="top-start">
            {/* Mantine's way: the tooltip around the menu's target. */}
            {limited(<Menu.Target>{slot}</Menu.Target>, frame.limits ?? [])}
            <Menu.Dropdown className={classes.frameMenu}>
              <Menu.Item leftSection={<LibraryBig size={16} />} onClick={frame.onFromLibrary}>
                From library
              </Menu.Item>
              <Menu.Item leftSection={<Sparkles size={16} />} onClick={frame.onGenerate}>
                Generate
              </Menu.Item>
              <Menu.Item leftSection={<Upload size={16} />} onClick={chooser.onClick}>
                Upload
              </Menu.Item>
              <Menu.Item leftSection={<Box size={16} />} onClick={frame.onFromProduct}>
                From product
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </FileButton>
      {view === null ? null : (
        <UnstyledButton
          className={classes.thumbRemove}
          aria-label={`Remove the ${lower}`}
          onClick={frame.onRemove}
        >
          <X size={14} strokeWidth={2.5} aria-hidden />
        </UnstyledButton>
      )}
    </span>,
  );
}

/** The frame's tile with live's limits as its tooltip, where the mode has any. */
function limited(tile: ReactElement, limits: string[]): ReactElement {
  if (limits.length === 0) return tile;
  return (
    <Tooltip
      label={limits.map((line) => (
        <div key={line}>{line}</div>
      ))}
      multiline
      withArrow
    >
      {tile}
    </Tooltip>
  );
}
